import React from 'react';
import { Grid, Box, Typography } from '@mui/material';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from './InspectionRadioGroup';
import AgeSelect from './AgeSelect';
import ManufacturerSelect from './ManufacturerSelect';
import {
  conditionOptions,
  conditionWithNaOptions,
  yesNoOptions,
  plumbingAgeOptions,
  PLUMBING_AGE_COLOR_MAP,
  PLUMBING_AGE_CONDITION_MAP,
  PLUMBING_SYSTEM_TYPE_OPTIONS,
} from '../config/options';
import { subHeaderSx } from './inspectionReportForm/inspectionReportFormStyles';

export interface PlumbingSystemValues {
  id: string;
  location: string;
  manufacturer: string;
  age: string;
  ageCondition: string;
  systemType: string;
  overallVisualCondition: string;
  expansionTankNa: boolean;
  expansionTankCondition: string;
  visibleLeaksOrRust: string;
  properClearance: string;
  recommendedAnnualMaintenance: string;
  installedCoupon: string;
  laundryTray: string;
  notes: string;
}

export function createDefaultPlumbingSystem(): PlumbingSystemValues {
  return {
    id: crypto.randomUUID(),
    location: '',
    manufacturer: '',
    age: '',
    ageCondition: '',
    systemType: '',
    overallVisualCondition: '',
    expansionTankNa: false,
    expansionTankCondition: '',
    visibleLeaksOrRust: '',
    properClearance: '',
    recommendedAnnualMaintenance: '',
    installedCoupon: '',
    laundryTray: '',
    notes: '',
  };
}

interface PlumbingSystemFieldsProps {
  values: PlumbingSystemValues;
  onChange: (field: keyof PlumbingSystemValues, value: any) => void;
  manufacturers: { value: string; label: string }[];
}

const PlumbingSystemFields: React.FC<PlumbingSystemFieldsProps> = ({ values, onChange, manufacturers }) => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <TextInput label="Location" value={values.location} valueChanged={(val) => onChange('location', val)} />
      </Grid>
    </Grid>

    <Typography sx={subHeaderSx}>Type*</Typography>
    <Box sx={{ mb: 2 }}>
      <InspectionRadioGroup
        radioOptions={PLUMBING_SYSTEM_TYPE_OPTIONS}
        value={values.systemType}
        setValue={(val) => onChange('systemType', val)}
      />
    </Box>

    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={6}>
        <ManufacturerSelect
          value={values.manufacturer}
          onChange={(val) => onChange('manufacturer', val)}
          options={manufacturers}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AgeSelect
          value={values.age}
          onChange={(val, condition) => {
            onChange('age', val);
            onChange('ageCondition', condition);
          }}
          options={plumbingAgeOptions}
          colorMap={PLUMBING_AGE_COLOR_MAP}
          conditionMap={PLUMBING_AGE_CONDITION_MAP}
        />
      </Grid>
    </Grid>

    <Typography sx={subHeaderSx}>Inspection Details</Typography>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Overall/Visual Condition"
          radioOptions={conditionOptions}
          value={values.overallVisualCondition}
          setValue={(val) => onChange('overallVisualCondition', val)}
        />
      </Grid>
      {values.systemType === 'tank_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Expansion Tank"
            radioOptions={conditionWithNaOptions}
            value={values.expansionTankCondition}
            setValue={(val) => onChange('expansionTankCondition', val)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Visible Leaks or Rust"
          radioOptions={yesNoOptions}
          value={values.visibleLeaksOrRust}
          setValue={(val) => onChange('visibleLeaksOrRust', val)}
          invertYesNoColors
        />
      </Grid>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Proper Clearance Around Equipment"
          radioOptions={yesNoOptions}
          value={values.properClearance}
          setValue={(val) => onChange('properClearance', val)}
        />
      </Grid>
      {values.systemType === 'tankless_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Recommended Annual Maintenance"
            radioOptions={yesNoOptions}
            value={values.recommendedAnnualMaintenance}
            setValue={(val) => onChange('recommendedAnnualMaintenance', val)}
          />
        </Grid>
      )}
      {values.systemType === 'tank_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Installed $50 Coupon"
            radioOptions={yesNoOptions}
            value={values.installedCoupon}
            setValue={(val) => onChange('installedCoupon', val)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Laundry Tray"
          radioOptions={conditionWithNaOptions}
          value={values.laundryTray}
          setValue={(val) => onChange('laundryTray', val)}
        />
      </Grid>
    </Grid>

    <TextInput
      label="Plumbing Notes"
      value={values.notes}
      valueChanged={(val) => onChange('notes', val)}
      multiline
      rows={3}
    />
  </Box>
);

export default PlumbingSystemFields;
